<template>
  <div class="food-safety-inspection">
    <div class="section-header">
      <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M9 11l3 3L22 4"></path>
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
      </svg>
      <h3>食安检查与培训</h3>
    </div>

    <div class="inspection-content">
      <div class="sub-header">
        <span class="sub-dot"></span>
        <h4>食安检查</h4>
      </div>
      <div class="inspection-body">
        <div class="completion-rate">
        <div class="rate-ring">
          <svg viewBox="0 0 100 100" class="rate-svg">
            <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(99,179,237,0.15)" stroke-width="6"/>
            <circle
              cx="50" cy="50" r="42"
              fill="none"
              stroke="#48bb78"
              stroke-width="6"
              stroke-linecap="round"
              :stroke-dasharray="dashArray"
              stroke-dashoffset="0"
              transform="rotate(-90 50 50)"
            />
          </svg>
          <div class="rate-text">
            <span class="rate-value">{{ inspection.completion_rate }}%</span>
            <span class="rate-label">完成率</span>
          </div>
        </div>
      </div>

      <div class="stats-grid">
        <div class="stat-card">
          <span class="stat-card-value">{{ inspection.plan_qty }}<small>项</small></span>
          <span class="stat-card-label">巡查计划</span>
        </div>
        <div class="stat-card stat-danger">
          <span class="stat-card-value">{{ inspection.abnormal_qty }}<small>项</small></span>
          <span class="stat-card-label">异常上报</span>
        </div>
        <div class="stat-card stat-success">
          <span class="stat-card-value">{{ inspection.completed_qty }}<small>项</small></span>
          <span class="stat-card-label">已完成</span>
        </div>
        <div class="stat-card stat-warning">
          <span class="stat-card-value">{{ inspection.disposal_rate }}<small>%</small></span>
          <span class="stat-card-label">处置率</span>
        </div>
      </div>
    </div>
    </div>

    <div class="chart-wrapper">
      <div class="sub-header">
        <span class="sub-dot"></span>
        <h4>食安培训</h4>
      </div>
      <svg viewBox="0 0 340 130" class="chart-svg" preserveAspectRatio="none">
        <line x1="50" y1="10" x2="50" y2="105" stroke="rgba(99,179,237,0.15)" stroke-width="1"/>

        <line
          v-for="(g, gi) in yGridLines"
          :key="'yg'+gi"
          :x1="50" :y1="g.y" :x2="330" :y2="g.y"
          stroke="rgba(99,179,237,0.06)"
          stroke-width="1"
        />

        <text
          v-for="(g, gi) in yGridLines"
          :key="'yt'+gi"
          :x="44"
          :y="g.y + 4"
          text-anchor="end"
          fill="#718096"
          font-size="9"
        >{{ g.label }}</text>

        <polyline
          v-if="chartPoints"
          :points="chartPoints"
          fill="none"
          stroke="#63b3ed"
          stroke-width="2"
          stroke-linejoin="round"
        />

        <g v-if="chartDots.length" fill="#48bb78">
          <circle
            v-for="(dot, idx) in chartDots"
            :key="idx"
            :cx="dot.x"
            :cy="dot.y"
            r="3"
          />
        </g>

        <g v-if="chartFill" fill="url(#chartGrad)">
          <polygon :points="chartFill" />
        </g>

        <text
          v-for="(dot, idx) in chartDots"
          :key="'xl'+idx"
          :x="dot.x"
          :y="110"
          text-anchor="middle"
          fill="#718096"
          font-size="9"
        >{{ trainingDetail[idx] && trainingDetail[idx].participant_qty || '' }}</text>

        <defs>
          <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#63b3ed" stop-opacity="0.3"/>
            <stop offset="100%" stop-color="#63b3ed" stop-opacity="0"/>
          </linearGradient>
        </defs>
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FoodSafetyInspection',
  props: {
    inspection: {
      type: Object,
      default: () => ({})
    },
    trainingDetail: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    circumference() {
      return 2 * Math.PI * 42
    },
    dashArray() {
      const pct = this.inspection.completion_rate || 0
      const fill = (pct / 100) * this.circumference
      return `${fill} ${this.circumference - fill}`
    },
    chartData() {
      if (!this.trainingDetail.length) return []
      return this.trainingDetail.map(item => item.pass_rate || 0)
    },
    yGridLines() {
      const lines = []
      const steps = [0, 25, 50, 75, 100]
      const top = 15
      const bottom = 95
      const range = bottom - top
      steps.forEach(pct => {
        lines.push({
          y: bottom - (pct / 100) * range,
          label: pct + '%'
        })
      })
      return lines
    },
    chartPoints() {
      const data = this.chartData
      if (!data.length) return ''
      const left = 55
      const right = 325
      const top = 15
      const bottom = 95
      const w = right - left
      const h = bottom - top
      const stepX = data.length > 1 ? w / (data.length - 1) : w / 2
      const maxVal = 100
      return data.map((v, i) => {
        const x = left + i * stepX
        const y = bottom - (v / maxVal) * h
        return `${x},${y}`
      }).join(' ')
    },
    chartDots() {
      const data = this.chartData
      if (!data.length) return []
      const left = 55
      const right = 325
      const top = 15
      const bottom = 95
      const w = right - left
      const h = bottom - top
      const stepX = data.length > 1 ? w / (data.length - 1) : w / 2
      const maxVal = 100
      return data.map((v, i) => ({
        x: left + i * stepX,
        y: bottom - (v / maxVal) * h
      }))
    },
    chartFill() {
      const data = this.chartData
      if (!data.length) return ''
      const left = 55
      const right = 325
      const top = 15
      const bottom = 95
      const w = right - left
      const h = bottom - top
      const stepX = data.length > 1 ? w / (data.length - 1) : w / 2
      const maxVal = 100
      const points = data.map((v, i) => {
        const x = left + i * stepX
        const y = bottom - (v / maxVal) * h
        return `${x},${y}`
      }).join(' ')
      return `${left},${bottom} ${points} ${right},${bottom}`
    }
  }
}
</script>

<style scoped>
.food-safety-inspection {
  height: 100%;
  background: rgba(26, 54, 93, 0.6);
  border-radius: 12px;
  padding: 14px;
  border: 1px solid rgba(99, 179, 237, 0.2);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
  flex-shrink: 0;
}

.section-header .icon {
  width: 18px;
  height: 18px;
  color: #63b3ed;
  flex-shrink: 0;
}

h3 {
  font-size: 14px;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0;
}

.inspection-content {
  flex-shrink: 0;
  margin-bottom: 10px;
}

.sub-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.sub-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #63b3ed;
}

.sub-header h4 {
  font-size: 12px;
  font-weight: 600;
  color: #63b3ed;
  margin: 0;
}

.inspection-body {
  display: flex;
  align-items: center;
  gap: 12px;
}

.completion-rate {
  flex: 0 0 30%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rate-ring {
  position: relative;
  width: 80px;
  height: 80px;
}

.rate-svg {
  width: 80px;
  height: 80px;
}

.rate-text {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.rate-value {
  font-size: 20px;
  font-weight: 700;
  color: #48bb78;
  line-height: 1;
}

.rate-label {
  font-size: 10px;
  color: #a0aec0;
  margin-top: 2px;
}

.stats-grid {
  flex: 0 0 70%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 6px;
}

.stat-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px;
}

.stat-card-value {
  font-size: 20px;
  font-weight: 700;
  color: #63b3ed;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-card-value small {
  font-size: 11px;
  font-weight: 500;
  color: #a0aec0;
  margin-left: 1px;
}

.stat-card.stat-danger .stat-card-value {
  color: #fc8181;
}

.stat-card.stat-success .stat-card-value {
  color: #48bb78;
}

.stat-card.stat-warning .stat-card-value {
  color: #f6ad55;
}

.stat-card-label {
  font-size: 12px;
  color: #a0aec0;
}

.chart-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.chart-svg {
  flex: 1;
  width: 100%;
  min-height: 0;
}

.chart-labels {
  display: flex;
  justify-content: space-around;
  padding: 0 55px 0 55px;
  flex-shrink: 0;
}

.chart-label {
  font-size: 10px;
  color: #718096;
  text-align: center;
}
</style>
